import { Card, Flex, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { CustomTableHeader } from '../custom-table-header/custom-table-header';
import { Button } from '../button/button';
import RefreshArrow from '@/assets/icons/refresh-ccw-01.svg?react';
import styles from './maintenances-header.module.scss';
import { CallMetricFilters } from '../call-metric-filters/call-metric-filters';
import { MaintenancesFilters } from '@/services/maintenances-service/maintenances-service.types';
import Search from '@/assets/icons/search-md.svg?react';
import Dropdown from '../form/dropdown/dropdown';
import { useUser } from '@/hooks/use-user/use-user';
import { useDebouncedFilterQuery } from '@/hooks/use-debounced-filter-query/use-debounced-filter-query';
import {
  useCreatedByOptions,
  usePriorityOptions,
  useStatusOptions,
} from './maintenances-header.constants';
import { CustomRangePicker } from '../custom-range-picker/custom-range-picker';
import dayjs, { Dayjs } from 'dayjs';
import { CustomFilters } from '../custom-filters/custom-filters';

interface MaintenancesHeaderProps {
  companiesList: string[];
  workOrders: number;
  onRefreshData?: () => void;
  filters: MaintenancesFilters;
  onChangeFilters: (filters: MaintenancesFilters) => void;
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onDateRangeClear: () => void;
  onChangeDateRangeFilter: (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => void;
  onSearchQueryChange: (query: string) => void;
  searchResults?: string[];
  isSearchLoading?: boolean;
  searchError?: Error | null;
}

export function MaintenancesHeader({
  onRefreshData,
  filters,
  onChangeFilters,
  companiesList,
  workOrders,
  dates,
  setDates,
  onDateRangeClear,
  onChangeDateRangeFilter,
  onSearchQueryChange,
  searchResults,
  isSearchLoading,
  searchError,
}: MaintenancesHeaderProps) {
  const { t } = useTranslation(['maintenances', 'common']);
  const { isFetched } = useUser();
  const { filterValue, onQueryChange } = useDebouncedFilterQuery(filters, onChangeFilters);
  const statusOptions = useStatusOptions();
  const priorityOptions = usePriorityOptions();
  const createdByOptions = useCreatedByOptions();

  const handlePriorityChange = (value: string) => {
    onChangeFilters({ ...filters, priority: value });
  };

  const handleStatusChange = (value: string) => {
    onChangeFilters({ ...filters, status: value });
  };

  const handleCreatedByChange = (value: string) => {
    onChangeFilters({ ...filters, created_by: value });
  };

  return (
    <Card className={styles.container} style={{ padding: '16px 24px' }}>
      <Flex wrap="wrap" gap={20} className={styles.topHeaderContainer}>
        <Flex align="center" className={styles.maintenancesCountSectionContainer}>
          <Flex gap={30} align="center">
            <Button
              aria-label={t('maintenances:header.refresh')}
              buttonVariant="secondary"
              onClick={onRefreshData}
              icon={<RefreshArrow stroke={styles.iconColor} />}
            />
            <CustomTableHeader
              textHeader1={t('maintenances:header.countLine1')}
              textHeader2={t('maintenances:header.countLine2')}
              count={workOrders}
            />
          </Flex>

          <Flex className={styles.filtersSectionContainer}>
            <CallMetricFilters
              value={{ time_range: filters.time_range }}
              onChange={(value) => {
                onChangeFilters({
                  ...filters,
                  time_range: value.time_range || '7d',
                  start_time: undefined,
                  end_time: undefined,
                });
                setDates([null, null]);
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CustomRangePicker
                dates={dates}
                setDates={setDates}
                onClear={onDateRangeClear}
                format="YYYY-MM-DD"
                onChange={onChangeDateRangeFilter}
                className={
                  filters.start_time !== undefined && filters.end_time !== undefined
                    ? styles.activeDatePicker
                    : ''
                }
                value={
                  filters.start_time && filters.end_time
                    ? [dayjs(filters.start_time), dayjs(filters.end_time)]
                    : [undefined, undefined]
                }
              />
            </div>
          </Flex>
        </Flex>

        <Flex className={styles.dropdownContainer} gap={10}>
          <Dropdown
            className={styles.dropdown}
            wrapperClassName={styles.wrapperClassName}
            dropdownListClass={styles.typeDropdownList}
            onChange={handleStatusChange}
            placeholder={t('maintenances:fields.status')}
            options={statusOptions}
            value={filters.status}
          />
          <Dropdown
            className={styles.dropdown}
            wrapperClassName={styles.wrapperClassName}
            dropdownListClass={styles.typeDropdownList}
            onChange={handlePriorityChange}
            placeholder={t('maintenances:fields.priority')}
            options={priorityOptions}
            value={filters.priority}
          />
          <Dropdown
            className={styles.dropdown}
            wrapperClassName={styles.wrapperClassName}
            dropdownListClass={styles.typeDropdownList}
            onChange={handleCreatedByChange}
            placeholder={t('maintenances:fields.createdBy')}
            options={createdByOptions}
            value={filters.created_by}
          />

          {isFetched && (
            <CustomFilters
              customClassName={styles.customSelectClassName}
              companiesList={companiesList}
              filters={filters}
              defaultCompanyValue={undefined}
              onChangeFilters={(updatedFilters) => {
                onChangeFilters({
                  ...filters,
                  company_name: updatedFilters.company_name,
                });
              }}
              onSearchQueryChange={onSearchQueryChange}
              searchResults={searchResults}
              isSearchLoading={isSearchLoading}
              searchError={searchError}
            />
          )}
        </Flex>
      </Flex>

      <Flex
        gap={20}
        className={styles.bottomHeaderContainer}
        wrap="wrap"
        justify="end"
        align="center"
      >
        <Flex vertical gap={5} className={styles.searchContainer}>
          <Input
            onChange={(event) => onQueryChange(event.target.value)}
            value={filterValue}
            placeholder={t('common:actions.search')}
            prefix={<Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />}
          />
        </Flex>
      </Flex>
    </Card>
  );
}
